import { isNonEmpty } from '@sgme/fp';
import classnames from 'classnames';
import { useGimmickSwitchInfos } from 'components/common-form/hooks/useGimmickSwitchInfos';
import { FC, useMemo } from 'react';
import type { GimmicksByName, ProductGraph } from 'state/product-description/model';
import type { BatchWorkspaceVersion, QuoteWorkspaceVersion } from 'state/userPreferences/userPreferences.model';
import type { QuoteWorkspaceType } from 'state/workspaces/model';

import { FormBuilder } from './builder/form/FormBuilder';
import { CrossBrowserDirtyWarning } from './CrossBrowserDirtyWarning';
import { useProductView } from './hooks/useProductView';

type MainFormProps = Readonly<{
  tabId: string;
  productDescription: ProductGraph;
  gimmickToggleFields?: string[];
  gimmicksDescription?: GimmicksByName;
  isNotEditable: boolean;
  canReloadSession: boolean;
  product: string;
  isUserInternal: boolean;
  crossBrowserDirty: boolean;
  workspaceType: QuoteWorkspaceType;
  quoteWorkspaceVersion: QuoteWorkspaceVersion;
  batchWorkspaceVersion: BatchWorkspaceVersion;
  mustForceAdvancedMode: boolean;
  onSessionReload: () => void;
}>;

export const MainForm: FC<MainFormProps> = ({
  tabId,
  gimmickToggleFields,
  gimmicksDescription,
  productDescription,
  isNotEditable,
  product,
  isUserInternal,
  crossBrowserDirty,
  onSessionReload,
  workspaceType,
  canReloadSession,
  quoteWorkspaceVersion,
  batchWorkspaceVersion,
  mustForceAdvancedMode,
}) => {
  const productView = useProductView();
  const [selectedGimmicks] = useGimmickSwitchInfos(gimmickToggleFields ?? []);
  const gimmicksBlocks: ProductGraph = useMemo(
    () => (gimmicksDescription !== undefined ? selectedGimmicks?.map(name => gimmicksDescription[name]) : []),
    [gimmicksDescription, selectedGimmicks],
  );
  const standardMode = quoteWorkspaceVersion === 'Standard' && mustForceAdvancedMode === false;
  return (
    <>
      <CrossBrowserDirtyWarning
        isDirty={crossBrowserDirty ?? false}
        onReload={onSessionReload}
        canReload={canReloadSession}
      />
      <div
        className={classnames('p-2 bg-lvl3 toggle-coupon', {
          'static-form--frozen': isNotEditable,
          'static-form__css-grid': workspaceType === 'static',
          'dynamic-form__css-grid': workspaceType === 'dynamic',
          'view-default': productView === 'Default',
          'standard-mode': standardMode,
        })}
        data-product={product}
        data-isuserinternal={isUserInternal}
      >
        <FormBuilder
          tabId={tabId}
          rootNodes={productDescription}
          workspaceType={workspaceType}
          quoteWorkspaceVersion={quoteWorkspaceVersion}
          batchWorkspaceVersion={batchWorkspaceVersion}
          mustForceAdvancedMode={mustForceAdvancedMode}
        />
        {isNonEmpty(gimmicksBlocks) && (
          <div id="Gimmicks">
            <FormBuilder
              tabId={tabId}
              rootNodes={gimmicksBlocks}
              workspaceType={workspaceType}
              quoteWorkspaceVersion={quoteWorkspaceVersion}
              batchWorkspaceVersion={batchWorkspaceVersion}
              mustForceAdvancedMode={mustForceAdvancedMode}
            />
          </div>
        )}
      </div>
    </>
  );
};

MainForm.displayName = 'MainForm';
